import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { TagChip, type TagChipTag } from './TagChip';
import { addSessionTag, removeSessionTag } from '@/api/sessions';
import { ConsoleContext } from '@/console/ConsoleContext';
import { httpStatus } from '@/console/viewer';
import { useT } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';

interface TagPickerProps {
  sessionId: string;
  /** The row's tag keys as the server last said them (the store row). */
  selected: readonly string[];
  /**
   * The viewer may not edit this conversation's tags (holds only workspace.viewAll, a teammate owns the conversation,
   * its owner is not yet verified...). The chips stay visible; every control that edits is gone. UX only: the server
   * re-checks `workspace.handle` and ownership per request.
   */
  readOnly: boolean;
  /** Distinguishes two pickers on one screen (the chat header and the contact panel) in test ids. */
  testIdPrefix?: string;
}

/**
 * The tags on ONE conversation, with the control that attaches and detaches them (ADR-033 D8).
 *
 * <b>EXISTING tags only.</b> The catalog is the tenant admin's, in the Admin Portal; this component lists its ACTIVE
 * tags and toggles them. There is deliberately no create, rename or delete path and no text field that could become
 * one: the search box only FILTERS, and Enter on a query that matches nothing does nothing. A hint says where tags are
 * managed.
 *
 * <b>Optimistic, but never a guess that outlives the server.</b> A toggle shows its result at once in a LOCAL overlay,
 * calls the per-key endpoint (POST attach / DELETE detach, both idempotent) and then applies the SERVER'S post-image
 * through the store (`setConversationTags`, which only moves forward on `tagsVersion`). A failure drops the overlay -
 * the chip goes back exactly as it was - and says so inline. The overlay is not written to the store because the store
 * refuses a same-version write by design (a late push must not roll tags back). A key with a request in flight cannot
 * be toggled again, so two clicks never race.
 *
 * <b>A refusal re-reads the row.</b> A 404 ("Session not available": the conversation became someone else's) re-reads
 * the row through `refreshRow`, which hides it if it is no longer visible - the console learns it from the server.
 */
export const TagPicker: React.FC<TagPickerProps> = ({ sessionId, selected, readOnly, testIdPrefix = 'tags' }) => {
  const { t } = useT();
  // The shell's row re-read, used only to learn the truth after a 404. Optional on purpose: the panel is also mounted
  // standalone (tests, storybook-style hosts) and a missing shell must cost the re-read, never the whole editor.
  const refreshRow = useContext(ConsoleContext)?.refreshRow;
  const catalog = useConversationStore((s) => s.tagCatalog);
  const setConversationTags = useConversationStore((s) => s.setConversationTags);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // key -> the state the viewer asked for, while its request is in flight.
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [failed, setFailed] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Mirror of `pending`'s keys readable synchronously, so a double click in one tick cannot start two requests.
  const inFlight = useRef<Set<string>>(new Set());
  const popId = useId();

  // A picker that becomes read-only (the owner changed under it) closes and forgets its half-typed filter.
  useEffect(() => {
    if (readOnly) {
      setOpen(false);
      setQuery('');
    }
  }, [readOnly]);

  // Focus the filter when the popover opens; Escape and a press outside close it.
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const isOn = (key: string): boolean => (key in pending ? pending[key] : selected.includes(key));

  // The chips: the row's keys with the in-flight changes laid over them. A key the catalog does not know renders as
  // its raw key, muted - never hidden (a retired tag on an old conversation is still a fact about it).
  const shownKeys = useMemo(() => {
    const keys = selected.filter((k) => pending[k] !== false);
    for (const [k, on] of Object.entries(pending)) if (on && !keys.includes(k)) keys.push(k);
    return keys;
  }, [selected, pending]);
  const chipFor = (key: string): TagChipTag => {
    const def = catalog.find((x) => x.key === key);
    return def ? def : { key, label: key, unknown: true };
  };

  // ACTIVE tags only, filtered by label (case-insensitive). Filtering is the search box's whole job.
  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog.filter((c) => c.isActive && (q === '' || c.label.toLowerCase().includes(q)));
  }, [catalog, query]);
  const catalogEmpty = !catalog.some((c) => c.isActive);

  const toggle = async (key: string) => {
    if (readOnly || inFlight.current.has(key)) return;
    const wasOn = isOn(key);
    inFlight.current.add(key);
    setFailed(false);
    setPending((p) => ({ ...p, [key]: !wasOn }));
    try {
      const res = wasOn ? await removeSessionTag(sessionId, key) : await addSessionTag(sessionId, key);
      // The server's post-image, never our guess. The store moves forward only: a newer push already applied wins.
      setConversationTags(sessionId, res.tags, res.tagsVersion);
    } catch (err) {
      setFailed(true);
      // 404 = "Session not available": it is not ours to see any more. Re-read it; the shell hides it if so.
      if (httpStatus(err) === 404) void refreshRow?.(sessionId);
    } finally {
      inFlight.current.delete(key);
      setPending((p) => {
        const { [key]: _gone, ...rest } = p;
        return rest;
      });
    }
  };

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    setQuery('');
    if (restoreFocus) addRef.current?.focus();
  };

  const focusOption = (delta: 1 | -1 | 'first' | 'last') => {
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    let next: number;
    if (delta === 'first') next = 0;
    else if (delta === 'last') next = items.length - 1;
    else next = at === -1 ? (delta === 1 ? 0 : items.length - 1) : Math.min(items.length - 1, Math.max(0, at + delta));
    items[next].focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close(true);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusOption(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusOption(-1);
    }
  };

  const id = (s: string) => `${testIdPrefix}-${s}`;

  return (
    <div className="tags-row" ref={rootRef} data-testid={id('row')}>
      <Icon name="tag" size={14} className="tags-ico" />
      {shownKeys.map((k) => (
        <TagChip key={k} tag={chipFor(k)} onRemove={readOnly ? undefined : () => void toggle(k)} />
      ))}
      {shownKeys.length === 0 && readOnly && (
        <span className="tag-more" data-testid={id('none')}>
          {t('fr_no_tags')}
        </span>
      )}

      {!readOnly && (
        <button
          type="button"
          ref={addRef}
          className="tag-add"
          // No active tag to pick (the catalog is empty, or could not be read - a 404 answers as an empty catalog): the
          // button stays visible but INERT, and its tooltip says where tags come from, instead of opening an empty
          // popover. aria-disabled (not `disabled`) so it stays focusable and its tooltip reachable by keyboard.
          onClick={() => {
            if (catalogEmpty) return;
            if (open) close(false);
            else setOpen(true);
          }}
          aria-disabled={catalogEmpty ? true : undefined}
          title={catalogEmpty ? t('tags_admin_hint') : undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? popId : undefined}
          data-testid={id('add')}
        >
          <Icon name="plus" size={12} />
          {t('tags_add')}
        </button>
      )}

      {open && !readOnly && (
        <div
          id={popId}
          className="tag-pop"
          role="dialog"
          aria-label={t('tags_add')}
          onKeyDown={onKeyDown}
          data-testid={id('popover')}
        >
          {catalogEmpty ? (
            <div className="tag-pop-empty" data-testid={id('empty')}>
              {t('tags_none_catalog')}
            </div>
          ) : (
            <>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Enter attaches / detaches the first MATCHING existing tag; with nothing matching it does nothing.
                  // There is no create path: the field only filters.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (options.length > 0) void toggle(options[0].key);
                  }
                }}
                placeholder={t('tags_search_ph')}
                aria-label={t('tags_search_ph')}
                data-testid={id('search')}
              />
              <ul ref={listRef} role="listbox" aria-multiselectable="true" aria-label={t('tags_add')}>
                {options.map((c) => {
                  const on = isOn(c.key);
                  return (
                    <li key={c.key} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={on}
                        aria-busy={c.key in pending}
                        className={'tag-opt' + (on ? ' on' : '')}
                        onClick={() => void toggle(c.key)}
                        onKeyDown={(e) => {
                          if (e.key === 'Home') {
                            e.preventDefault();
                            focusOption('first');
                          } else if (e.key === 'End') {
                            e.preventDefault();
                            focusOption('last');
                          }
                        }}
                        style={/^#[0-9a-f]{3,8}$/i.test(c.color) ? ({ ['--tag' as string]: c.color } as React.CSSProperties) : undefined}
                        data-testid={`${testIdPrefix}-opt-${c.key}`}
                      >
                        <span className="tag-check" aria-hidden="true">
                          {on && <Icon name="check" size={11} />}
                        </span>
                        <span className="tag-dot" aria-hidden="true" />
                        <span dir="auto">{c.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {options.length === 0 && (
                <div className="tag-pop-empty" data-testid={id('no-match')}>
                  {t('tags_no_match')}
                </div>
              )}
            </>
          )}
          <div className="tag-pop-hint" data-testid={id('hint')}>
            {t('tags_admin_hint')}
          </div>
        </div>
      )}

      {failed && (
        <span role="alert" className="tag-err" data-testid={id('error')}>
          {t('tags_save_failed')}
        </span>
      )}
    </div>
  );
};
